import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent, type RefObject } from "react";
import { getSuggestions, QUESTION_MAX_LENGTH, QUESTION_MIN_LENGTH } from "@/api/assistant";
import { useFocusTrap, useLockBody } from "@/hooks/useDialog";
import { useMediaQuery } from "@/hooks/useMotion";
import { useResource } from "@/hooks/useResource";
import { cn } from "@/lib/cn";
import { useChat, type AssistantMessage, type ChatMessage } from "@/state/ChatProvider";
import { Icon } from "@/components/ui/Icon";
import { Link } from "@/router/Router";
import { RichText } from "@/components/ScriptureText";
import { SITE } from "@/config/site";

/** The Miracle Assembly AI panel: floating on desktop, full screen on phones. */
export function AIChat() {
  const { open, setOpen, messages, responding, send, openAnswer, retry, giveFeedback, reset } = useChat();
  const [mounted, setMounted] = useState(open);
  const [visible, setVisible] = useState(false);
  const panel = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const isPhone = useMediaQuery("(max-width: 640px)");
  const close = () => setOpen(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const r = requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
      return () => cancelAnimationFrame(r);
    }
    setVisible(false);
    const t = window.setTimeout(() => setMounted(false), 360);
    return () => window.clearTimeout(t);
  }, [open]);

  useLockBody(mounted && isPhone);
  useFocusTrap(panel, open, close, input);

  // Keep the newest message in view.
  useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: messages.length > 1 ? "smooth" : "auto" });
  }, [messages.length, responding, mounted]);

  const suggestions = useResource(mounted ? "ai:suggestions" : null, (signal) => getSuggestions(signal), { cacheMs: 30 * 60_000 });

  if (!mounted) return null;
  const empty = messages.length === 0;

  return (
    <div ref={panel} className={cn("ai-panel", visible && "is-open")} role="dialog" aria-modal={isPhone} aria-labelledby="ai-title">
      <header className="ai-panel__header">
        <span className="ai-orb ai-orb--sm" aria-hidden>
          <Icon name="sparkle" size={16} />
        </span>
        <div className="ai-panel__heading">
          <h2 id="ai-title" className="ai-panel__title">
            Miracle Assembly AI
          </h2>
          <p className="ai-panel__sub">Answers from the church's own knowledge base</p>
        </div>
        {!empty ? (
          <button type="button" className="ai-panel__tool" onClick={reset} title="Start a new conversation">
            New chat
          </button>
        ) : null}
        <button type="button" className="icon-btn" onClick={close} aria-label="Close Miracle Assembly AI">
          <Icon name="close" size={20} />
        </button>
      </header>

      <div ref={scroller} className="ai-panel__scroll" aria-live="polite" aria-relevant="additions">
        {empty ? (
          <div className="ai-welcome">
            <p className="ai-welcome__hello">Peace be with you.</p>
            <p className="ai-welcome__title">What would you like to know?</p>
            <p className="ai-welcome__text">Ask about service times, locations, care groups, giving, events or what we believe.</p>
            <div className="ai-chips" aria-label="Suggested questions">
              {suggestions.status === "loading" && !suggestions.data
                ? Array.from({ length: 4 }, (_, i) => <span key={i} className="skeleton ai-chip-skeleton" aria-hidden />)
                : null}
              {(suggestions.data ?? []).slice(0, 8).map((s) => (
                <button key={s.id} type="button" className="ai-chip" onClick={() => openAnswer({ id: s.id, question: s.question })}>
                  {s.question}
                </button>
              ))}
              {suggestions.status === "error" || (suggestions.status === "success" && !suggestions.data?.length)
                ? FALLBACK_PROMPTS.map((q) => (
                    <button key={q} type="button" className="ai-chip" onClick={() => send(q)}>
                      {q}
                    </button>
                  ))
                : null}
            </div>
          </div>
        ) : (
          <ol className="ai-thread">
            {messages.map((m) => (
              <Message key={m.id} message={m} onRetry={retry} onFeedback={giveFeedback} onRelated={openAnswer} onNavigate={close} />
            ))}
            {responding ? (
              <li className="ai-msg ai-msg--assistant">
                <span className="typing" role="status" aria-label="Miracle Assembly AI is typing">
                  <span />
                  <span />
                  <span />
                </span>
              </li>
            ) : null}
          </ol>
        )}
      </div>

      <Composer onSend={send} disabled={false} inputRef={input} />
      <p className="ai-panel__foot">
        AI answers can be incomplete. For pastoral care, <Link to="/contact" onClick={close}>contact the church</Link>.
      </p>
    </div>
  );
}

const FALLBACK_PROMPTS = ["What time is Sunday service?", "Where is the church located?", "How do I join a care group?", "How can I give?"];

function Message({
  message: m,
  onRetry,
  onFeedback,
  onRelated,
  onNavigate,
}: {
  message: ChatMessage;
  onRetry: (id: string) => void;
  onFeedback: (id: string, helpful: boolean) => void;
  onRelated: (q: { id: string; question: string }) => void;
  onNavigate: () => void;
}) {
  if (m.role === "user") {
    return (
      <li className={cn("ai-msg ai-msg--user", m.status === "failed" && "is-failed")}>
        <div className="ai-bubble ai-bubble--user">{m.text}</div>
        {m.status === "failed" ? (
          <div className="ai-msg__error" role="alert">
            <span>{m.error ?? "This message wasn't sent."}</span>
            <button type="button" onClick={() => onRetry(m.id)}>
              <Icon name="refresh" size={14} /> Retry
            </button>
          </div>
        ) : null}
      </li>
    );
  }
  return (
    <li className={cn("ai-msg ai-msg--assistant", `ai-msg--${m.kind}`)}>
      <div className="ai-bubble ai-bubble--assistant">
        {m.kind === "unanswered" ? (
          <p className="ai-bubble__note">
            <Icon name="info" size={14} /> Passed to the team
          </p>
        ) : null}
        <RichText text={m.text} onNavigate={onNavigate} />
        {m.kind === "unanswered" ? (
          <p className="ai-bubble__aside">
            You can also email <a href={`mailto:${SITE.email}`}>{SITE.email}</a>.
          </p>
        ) : null}
      </div>
      {m.kind === "answered" && m.entry?.id ? <Feedback message={m} onFeedback={onFeedback} /> : null}
      {m.related.length ? (
        <div className="ai-related">
          <p className="ai-related__label">Related</p>
          {m.related.slice(0, 4).map((r) => (
            <button key={r.id} type="button" className="ai-chip ai-chip--small" onClick={() => onRelated(r)}>
              {r.question}
            </button>
          ))}
        </div>
      ) : null}
    </li>
  );
}

function Feedback({ message, onFeedback }: { message: AssistantMessage; onFeedback: (id: string, helpful: boolean) => void }) {
  if (message.feedback === "helpful" || message.feedback === "unhelpful") {
    return (
      <p className="ai-feedback ai-feedback--done" role="status">
        <Icon name="check" size={14} />
        {message.feedback === "helpful" ? "Thanks — glad that helped." : "Thanks. We've passed your question to the team so they can follow up."}
      </p>
    );
  }
  return (
    <div className="ai-feedback">
      <span>{message.confident === false ? "Did this answer your question?" : "Was this helpful?"}</span>
      <button type="button" disabled={message.feedback === "sending"} onClick={() => onFeedback(message.id, true)} aria-label="Yes, this was helpful">
        <Icon name="thumbUp" size={15} /> Yes
      </button>
      <button type="button" disabled={message.feedback === "sending"} onClick={() => onFeedback(message.id, false)} aria-label="No, this wasn't helpful">
        <Icon name="thumbDown" size={15} /> No
      </button>
      {message.feedback === "failed" ? <span className="ai-feedback__error">Couldn't send — try again.</span> : null}
    </div>
  );
}

function Composer({ onSend, disabled, inputRef }: { onSend: (text: string) => void; disabled: boolean; inputRef: RefObject<HTMLTextAreaElement | null> }) {
  const [text, setText] = useState("");
  const trimmed = text.trim();
  const tooShort = trimmed.length > 0 && trimmed.length < QUESTION_MIN_LENGTH;
  const canSend = trimmed.length >= QUESTION_MIN_LENGTH && !disabled;

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    if (!canSend) return;
    onSend(trimmed);
    setText("");
  };
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <form className="ai-composer" onSubmit={submit}>
      <label htmlFor="ai-input" className="sr-only">
        Ask Miracle Assembly AI
      </label>
      <textarea
        id="ai-input"
        ref={inputRef}
        rows={1}
        value={text}
        maxLength={QUESTION_MAX_LENGTH}
        placeholder="Ask a question…"
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKeyDown}
        aria-invalid={tooShort || undefined}
      />
      <button type="submit" className="ai-composer__send" disabled={!canSend} aria-label="Send question">
        <Icon name="send" size={18} />
      </button>
      {text.length > QUESTION_MAX_LENGTH - 60 ? (
        <span className="ai-composer__count" aria-live="polite">
          {QUESTION_MAX_LENGTH - text.length} left
        </span>
      ) : null}
    </form>
  );
}
